import { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { reportAPI } from '@/lib/api/reports'
import { projectAPI } from '@/lib/api/projects'
import type { ReportProfile } from '@/lib/types/report'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'

interface ProjectRow {
  id: string
  project_name: string
  database: string
}

interface NewReportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (reportId: string) => void
}

/** Template + project picker → POST /reports. The selected template's declared
 * project cardinality drives the selector: exact/1 behaves as a radio group,
 * exact/N and dynamic are ordered multi-selects (selection order is the
 * project order sent — comparison A = first picked). Backend errors render
 * verbatim. */
export function NewReportDialog({ open, onOpenChange, onCreated }: NewReportDialogProps) {
  const [profiles, setProfiles] = useState<ReportProfile[]>([])
  const [projects, setProjects] = useState<ProjectRow[]>([])
  const [profileId, setProfileId] = useState<string | null>(null)
  const [projectRefs, setProjectRefs] = useState<string[]>([])
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    if (!open) return
    setError(null)
    setProfileId(null)
    setProjectRefs([])
    setName('')
    reportAPI.listReportTemplates().then((r) => setProfiles(r.data.profiles)).catch(() => toast.error('Could not load templates'))
    projectAPI.listProjects({ limit: 100 }).then((r) => setProjects(r.data.projects)).catch(() => toast.error('Could not load projects'))
  }, [open])

  const selectedProfile = profiles.find((p) => p.id === profileId) ?? null
  const card = selectedProfile?.projects ?? null
  const isSingle = card !== null && card.mode === 'exact' && card.count === 1
  const capacity =
    card === null ? 0 : card.mode === 'exact' ? (card.count ?? 1) : (card.max ?? Number.POSITIVE_INFINITY)
  const countOk =
    card !== null &&
    (card.mode === 'exact'
      ? projectRefs.length === card.count
      : projectRefs.length >= card.min && (card.max === null || projectRefs.length <= card.max))

  const pickTemplate = (p: ReportProfile) => {
    setProfileId(p.id)
    const cap = p.projects.mode === 'exact' ? (p.projects.count ?? 1) : (p.projects.max ?? Number.POSITIVE_INFINITY)
    setProjectRefs((prev) => (Number.isFinite(cap) ? prev.slice(0, cap) : prev))
  }

  const toggleProject = (id: string) => {
    if (isSingle) {
      setProjectRefs([id])
      return
    }
    setProjectRefs((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : prev.length >= capacity ? prev : [...prev, id],
    )
  }

  const helper =
    card === null || isSingle
      ? null
      : card.mode === 'exact'
        ? `Selected ${projectRefs.length} of ${card.count}`
        : card.max !== null
          ? `Select ${card.min}–${card.max} projects — ${projectRefs.length} selected`
          : `Select at least ${card.min} projects — ${projectRefs.length} selected`

  const create = async () => {
    if (!profileId || !countOk) return
    setCreating(true)
    setError(null)
    try {
      const title = name.trim()
      const res = await reportAPI.createReport({
        profile_id: profileId,
        project_refs: projectRefs,
        ...(title ? { title } : {}),
      })
      onCreated(res.data.report_id)
      onOpenChange(false)
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
      const message =
        typeof detail === 'string'
          ? detail
          : ((detail as { message?: string } | undefined)?.message ?? 'Failed to create the report.')
      setError(message)
    } finally {
      setCreating(false)
    }
  }

  const SelectRow = ({
    role, checked, disabled, label, sublabel, badge, onPick,
  }: {
    role: 'radio' | 'checkbox'
    checked: boolean
    disabled?: boolean
    label: string
    sublabel: string
    badge?: number | null
    onPick: () => void
  }) => (
    <button
      type="button"
      role={role}
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onPick}
      className={cn(
        'flex w-full items-baseline justify-between gap-2 rounded-md border px-3 py-2 text-left text-sm',
        checked ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40',
        disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent',
      )}
    >
      <span className="flex min-w-0 items-baseline gap-2">
        {badge != null && (
          <span className="shrink-0 rounded bg-primary/10 px-1.5 text-xs font-semibold tabular-nums text-primary">
            {badge}
          </span>
        )}
        <span className="truncate font-medium text-foreground">{label}</span>
      </span>
      <span className="shrink-0 text-xs text-muted-foreground">{sublabel}</span>
    </button>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New report</DialogTitle>
          <DialogDescription>Pick a template and the project(s) it should assess.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Name</p>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" maxLength={200} />
        </div>
        <div className="space-y-1" role="radiogroup" aria-label="Template">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Template</p>
          {profiles.map((p) => (
            <SelectRow
              key={p.id}
              role="radio"
              checked={profileId === p.id}
              label={p.name}
              sublabel={p.min_tier != null && p.min_tier > 1 ? `tier ${p.min_tier}+` : `v${p.version}`}
              onPick={() => pickTemplate(p)}
            />
          ))}
        </div>
        <div
          className="mt-3 max-h-48 space-y-1 overflow-y-auto"
          role={isSingle ? 'radiogroup' : 'group'}
          aria-label={isSingle ? 'Project' : 'Projects'}
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {isSingle ? 'Project' : 'Projects'}
          </p>
          {helper && <p className="text-xs text-muted-foreground">{helper}</p>}
          {card === null ? (
            <p className="text-sm text-muted-foreground">Select a template first.</p>
          ) : (
            projects.map((p) => {
              const idx = projectRefs.indexOf(p.id)
              const checked = idx !== -1
              return (
                <SelectRow
                  key={p.id}
                  role={isSingle ? 'radio' : 'checkbox'}
                  checked={checked}
                  disabled={!checked && !isSingle && projectRefs.length >= capacity}
                  label={p.project_name}
                  sublabel={p.database}
                  badge={!isSingle && checked ? idx + 1 : null}
                  onPick={() => toggleProject(p.id)}
                />
              )
            })
          )}
        </div>
        {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={create} disabled={!profileId || !countOk || creating}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
